import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';

const HOLD_MS = 2000;

function HoldButton({ disabled, color, icon, tooltip, onConfirm }) {
    const [progress, setProgress] = useState(0);
    const [flash, setFlash] = useState(false);
    const rafRef = useRef(null);
    const startRef = useRef(0);
    const firedRef = useRef(false);
    const flashTimerRef = useRef(null);

    const stop = () => {
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        setProgress(0);
    };

    useEffect(() => () => {
        stop();
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    }, []);

    const triggerFlash = () => {
        setFlash(true);
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        // ~600ms = two pulses of 300ms each (matches CSS animation)
        flashTimerRef.current = setTimeout(() => setFlash(false), 620);
    };

    const start = (e) => {
        if (disabled) return;
        if (e && e.preventDefault) e.preventDefault();
        if (rafRef.current) return;
        firedRef.current = false;
        startRef.current = performance.now();
        setProgress(0);
        const step = (now) => {
            const elapsed = now - startRef.current;
            const pct = Math.min(100, (elapsed / HOLD_MS) * 100);
            setProgress(pct);
            if (pct >= 100) {
                if (!firedRef.current) {
                    firedRef.current = true;
                    stop();
                    triggerFlash();
                    onConfirm && onConfirm();
                }
                return;
            }
            rafRef.current = requestAnimationFrame(step);
        };
        rafRef.current = requestAnimationFrame(step);
    };

    const cancel = () => stop();

    const size = 36;
    const ringSize = 40;

    return (
        <Tooltip
            title={tooltip}
            arrow
            placement="top"
            slotProps={{
                tooltip: {
                    sx: {
                        bgcolor: 'warning.main',
                        color: 'warning.contrastText',
                        fontWeight: 600,
                    },
                },
                arrow: { sx: { color: 'warning.main' } },
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: ringSize,
                    height: ringSize,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <CircularProgress
                    variant="determinate"
                    value={progress}
                    size={ringSize}
                    thickness={3}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        color: `${color}.main`,
                        opacity: progress > 0 ? 1 : 0,
                        transition: 'opacity 120ms',
                        '& .MuiCircularProgress-circle': {
                            transition: 'none',
                        },
                    }}
                />
                <IconButton
                    color={color}
                    disabled={disabled}
                    onMouseDown={start}
                    onMouseUp={cancel}
                    onMouseLeave={cancel}
                    onTouchStart={start}
                    onTouchEnd={cancel}
                    onTouchCancel={cancel}
                    onContextMenu={(e) => e.preventDefault()}
                    sx={{
                        width: size,
                        height: size,
                        bgcolor: 'rgba(255, 255, 255, 0.12)',
                        color: '#fff',
                        transition: 'transform 150ms ease, background-color 150ms ease',
                        '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            transform: 'scale(1.35)',
                        },
                        '&:active': { transform: 'scale(1.2)' },
                        '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)' },
                        ...(flash && {
                            animation: 'armctrl-flash 300ms ease 2',
                            '@keyframes armctrl-flash': {
                                '0%, 100%': {
                                    bgcolor: 'rgba(255, 255, 255, 0.12)',
                                    boxShadow: 'none',
                                },
                                '50%': {
                                    bgcolor: `${color}.main`,
                                    boxShadow: (theme) => `0 0 12px 4px ${theme.palette[color].main}`,
                                },
                            },
                        }),
                    }}
                >
                    {icon}
                </IconButton>
            </Box>
        </Tooltip>
    );
}

export default function ArmControl({ selectedMavId, mavTimestamp }) {
    const [armed, setArmed] = useState(false);
    const [hasHeartbeat, setHasHeartbeat] = useState(false);
    const lastHbRef = useRef(0);
    const lastHbMsgRef = useRef(null);

    // Track when a fresh HEARTBEAT arrives (object reference change).
    useEffect(() => {
        const vehicle = window.mav?.[selectedMavId];
        const hb = vehicle?.HEARTBEAT;
        if (hb && hb !== lastHbMsgRef.current) {
            lastHbMsgRef.current = hb;
            lastHbRef.current = Date.now();
            setHasHeartbeat(true);
            setArmed((hb.base_mode & 0x80) !== 0);
        }
    }, [mavTimestamp, selectedMavId]);

    // Reset when switching vehicles.
    useEffect(() => {
        lastHbMsgRef.current = null;
        lastHbRef.current = 0;
        setHasHeartbeat(false);
        setArmed(false);
    }, [selectedMavId]);

    // Periodically check for heartbeat timeout (>3s -> OFFLINE).
    useEffect(() => {
        const HB_TIMEOUT_MS = 3000;
        const id = setInterval(() => {
            if (lastHbRef.current === 0) return;
            if (Date.now() - lastHbRef.current > HB_TIMEOUT_MS) {
                setHasHeartbeat(false);
                setArmed(false);
            }
        }, 500);
        return () => clearInterval(id);
    }, []);

    const disabled = !hasHeartbeat || selectedMavId == null || selectedMavId < 0;

    const doArm = () => {
        if (disabled) return;
        window.api.armDisarm?.(selectedMavId, true, false);
    };
    const doDisarm = () => {
        if (disabled) return;
        window.api.armDisarm?.(selectedMavId, false, false);
    };

    const statusColor = !hasHeartbeat
        ? 'rgba(180,180,180,0.85)'
        : armed
            ? 'rgba(220, 53, 69, 0.95)'
            : 'rgba(46, 160, 67, 0.9)';

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                bgcolor: 'rgba(0, 0, 0, 0.72)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 1,
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(2px)',
                pointerEvents: 'auto',
                p: 0.5,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    py: 0.5,
                    borderRadius: 0.75,
                    bgcolor: statusColor,
                    color: armed ? '#fff' : '#000',
                    minWidth: 78,
                    justifyContent: 'center',
                }}
            >
                <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5 }}>
                    {hasHeartbeat ? (armed ? 'ARMED' : 'DISARMED') : 'OFFLINE'}
                </Typography>
            </Box>
            {armed ? (
                <HoldButton
                    disabled={disabled}
                    color="warning"
                    icon={<LockIcon fontSize="small" />}
                    tooltip="Hold to Disarm"
                    onConfirm={doDisarm}
                />
            ) : (
                <HoldButton
                    disabled={disabled}
                    color="success"
                    icon={<LockOpenIcon fontSize="small" />}
                    tooltip="Hold to Arm"
                    onConfirm={doArm}
                />
            )}
        </Box>
    );
}
